/**
 * normalize-tool-result
 *
 * Shared normalisation helpers for tool results coming from annotations.
 * This turns provider-specific JSON blobs into stable shapes the UI can
 * render as rich interactive components.
 *
 * Primary responsibilities:
 * - Inspect tool annotation keys and raw result payloads
 * - Produce a typed NormalizedToolResult union with friendly fields
 * - Stay defensive around unknown / partial shapes
 *
 * Dependencies:
 * - None (pure parsing helpers)
 */

export type ToolResultKind =
  | 'web_search'
  | 'fetched_url'
  | 'weather'
  | 'finance_quote'
  | 'virtual_file_single'
  | 'virtual_file_list'
  | 'task'
  | 'task_list'
  | 'memory'
  | 'chat_management'
  | 'connected_services'
  | 'tool_management'
  | 'vector_memory'
  | 'unified_search'
  | 'chat_search'
  | 'bookmark_search'
  | 'starred_search'
  | 'image_generation'
  | 'video_generation'
  | 'sidecar_run'
  | 'spaces_entity'
  | 'spaces_entity_list'
  | 'spaces_task_support'
  | 'spaces_conversation_messages'
  | 'spaces_invitation'
  | 'spaces_status'
  | 'generic'

export interface NormalizedToolResultBase {
  kind: ToolResultKind
  /**
   * Original annotation key, e.g. webScraper__googleSearch
   */
  key: string
  /**
   * Short human label for this result instance.
   */
  label: string
}

export interface WebSearchNormalizedResult extends NormalizedToolResultBase {
  kind: 'web_search'
  query: string | null
  url?: string
  totalResults?: number
  raw: unknown
}

export interface FetchedUrlNormalizedResult extends NormalizedToolResultBase {
  kind: 'fetched_url'
  url: string
  title?: string
  markdown?: string
}

export interface WeatherNormalizedResult extends NormalizedToolResultBase {
  kind: 'weather'
  location?: string
  summary?: string
  temperatureC?: number
  temperatureF?: number
  raw: unknown
}

export interface FinanceQuoteNormalizedResult extends NormalizedToolResultBase {
  kind: 'finance_quote'
  symbol?: string
  name?: string
  price?: number
  change?: number
  changePercent?: number
  currency?: string
  raw: unknown
}

export interface VirtualFileSingleNormalizedResult extends NormalizedToolResultBase {
  kind: 'virtual_file_single'
  file: {
    id: string
    path: string
    name: string
    content?: string
  }
}

export interface VirtualFileListNormalizedResult extends NormalizedToolResultBase {
  kind: 'virtual_file_list'
  files: Array<{
    id: string
    path: string
    name: string
  }>
}

export interface TaskNormalizedResult extends NormalizedToolResultBase {
  kind: 'task' | 'task_list'
  tasks: Array<{
    id?: string
    title?: string
    status?: string
    due_date?: string | null
    created_at?: string | null
  }>
}

export type SpacesEntityType =
  | 'item'
  | 'space'
  | 'task'
  | 'note'
  | 'folder'
  | 'chat'
  | 'conversation'
  | 'member'
  | 'whiteboard'
  | 'design'
  | 'sheet'
  | 'sprint'
  | 'event'
  | 'file'
  | 'collection'
  | 'calendar'

export interface SpacesEntitySummary {
  id?: string
  type: SpacesEntityType
  title?: string
  description?: string
  url?: string
  status?: string
  priority?: string
  role?: string
  sharing?: string
  model?: string
  contextType?: string
  email?: string
  username?: string
  imageUrl?: string
  dueDate?: string | null
  updatedAt?: string | null
  createdAt?: string | null
  locked?: boolean
  archived?: boolean
  favorited?: boolean
}

export interface SpacesEntityNormalizedResult extends NormalizedToolResultBase {
  kind: 'spaces_entity' | 'spaces_entity_list'
  entityType: SpacesEntityType
  items: SpacesEntitySummary[]
}

export interface SpacesTaskSupportNormalizedResult
  extends NormalizedToolResultBase {
  kind: 'spaces_task_support'
  supportType: 'subtasks' | 'checklist'
  taskId?: string
  items: Array<{
    id?: string
    title?: string
    description?: string
    completed?: boolean
    dueDate?: string | null
  }>
}

export interface SpacesConversationMessagesNormalizedResult
  extends NormalizedToolResultBase {
  kind: 'spaces_conversation_messages'
  conversation?: SpacesEntitySummary
  messages: Array<{
    id?: string
    role?: string
    content?: string
    authorName?: string
    errorMessage?: string
    model?: string
    createdAt?: string | null
  }>
}

export interface SpacesInvitationNormalizedResult
  extends NormalizedToolResultBase {
  kind: 'spaces_invitation'
  message?: string
  createdCount?: number
  requestedCount?: number
  emailsSent?: string[]
  emailsFailed?: string[]
  validationErrors?: string[]
}

export interface SpacesStatusNormalizedResult extends NormalizedToolResultBase {
  kind: 'spaces_status'
  state: 'loading' | 'empty' | 'error' | 'success'
  title: string
  description?: string
  detailLines?: string[]
}

export interface MemoryNormalizedResult extends NormalizedToolResultBase {
  kind: 'memory'
  action: 'store' | 'list' | 'update' | 'delete' | 'unknown'
  items?: Array<{
    id?: string
    key?: string
    value?: string
    created_at?: string
  }>
  raw: unknown
}

export interface ChatManagementNormalizedResult extends NormalizedToolResultBase {
  kind: 'chat_management'
  action: 'get_chats' | 'get_messages' | 'pin_chat' | 'star_message' | 'unknown'
  chats?: Array<{
    id?: string
    name?: string
    pinned?: boolean
    model?: string
    created_at?: string
    updated_at?: string | null
  }>
  messages?: Array<{
    id?: string
    content?: string
    role?: string
    thumbs_up?: boolean | null
    created_at?: string
  }>
  raw: unknown
}

export interface PdfDocumentNormalizedResult extends NormalizedToolResultBase {
  kind: 'pdf_document'
  success?: boolean
  url?: string
  fileName: string
  message?: string
  error?: string
  /** Present when storage upload failed but the PDF still rendered. */
  pdfBase64?: string
  raw: unknown
}

export interface ConnectedServicesNormalizedResult extends NormalizedToolResultBase {
  kind: 'connected_services'
  operation?: 'listExtensions' | 'listActions' | 'executeAction' | 'checkToolkit' | 'unknown'
  success?: boolean
  error?: string
  timestamp?: string
  connected?: boolean
  toolkitSlug?: string
  connectionUrl?: string
  summary?: string
  setupCard?: {
    kind: 'composio_auth'
    title: string
    description: string
    toolkit_slug: string
    connection_url: string | null
    action_label: string
    logo?: string
  }
  extensions?: Array<{
    name?: string
    active?: boolean
  }>
  actions?: Array<{
    id?: string
    name?: string
    description?: string
  }>
  raw: unknown
}

export interface ToolManagementNormalizedResult extends NormalizedToolResultBase {
  kind: 'tool_management'
  action: 'disable' | 'enable' | 'list' | 'unknown'
  toolName?: string
  tools?: Array<{
    id?: string
    name?: string
    description?: string
    badge?: string | null
  }>
  raw: unknown
}

export interface VectorMemoryNormalizedResult extends NormalizedToolResultBase {
  kind: 'vector_memory'
  items?: Array<{
    key?: string
    content?: string
    created_at?: string
    updated_at?: string
    expiration?: string | null
  }>
  count?: number
  raw: unknown
}

export interface SearchNormalizedResult extends NormalizedToolResultBase {
  kind: 'unified_search' | 'chat_search' | 'bookmark_search' | 'starred_search'
  items: Array<{
    id?: string
    chat_id?: string
    message_id?: string
    title?: string
    snippet?: string
    created_at?: string
  }>
}

export interface MediaGenerationNormalizedResult extends NormalizedToolResultBase {
  kind: 'image_generation' | 'video_generation'
  items: Array<{
    url: string
    thumbnailUrl?: string
    prompt?: string
  }>
}

export interface SidecarRunNormalizedResult extends NormalizedToolResultBase {
  kind: 'sidecar_run'
  runId?: string
  status?: 'planning' | 'running' | 'completed' | 'error'
  executionMode?: 'parallel' | 'sequential'
  objective?: string
  reason?: string
  finalAnswer?: string
  summary?: string
  agentCount?: number
  successfulAgents?: number
  failedAgents?: number
  plannedWorkers?: Array<{
    id?: string
    source?: 'ad_hoc' | 'built_in_helper' | 'custom_helper'
    helperTemplateId?: string
    helperId?: string
    title?: string
    objective?: string
    deliverable?: string
    dependsOn?: string[]
  }>
  workers?: Array<{
    id?: string
    source?: 'ad_hoc' | 'built_in_helper' | 'custom_helper'
    helperTemplateId?: string
    helperId?: string
    title?: string
    objective?: string
    deliverable?: string
    status?: 'completed' | 'error'
    finalOutput?: string
    error?: string
  }>
  raw: unknown
}

export interface GenericNormalizedResult extends NormalizedToolResultBase {
  kind: 'generic'
  raw: unknown
}

export type NormalizedToolResult =
  | WebSearchNormalizedResult
  | FetchedUrlNormalizedResult
  | WeatherNormalizedResult
  | FinanceQuoteNormalizedResult
  | VirtualFileSingleNormalizedResult
  | VirtualFileListNormalizedResult
  | TaskNormalizedResult
  | SpacesEntityNormalizedResult
  | SpacesTaskSupportNormalizedResult
  | SpacesConversationMessagesNormalizedResult
  | SpacesInvitationNormalizedResult
  | SpacesStatusNormalizedResult
  | MemoryNormalizedResult
  | ChatManagementNormalizedResult
  | ConnectedServicesNormalizedResult
  | ToolManagementNormalizedResult
  | VectorMemoryNormalizedResult
  | SearchNormalizedResult
  | MediaGenerationNormalizedResult
  | SidecarRunNormalizedResult
  | PdfDocumentNormalizedResult
  | GenericNormalizedResult

// --------------

function tryParseJsonPayload(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  if (
    !(
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    )
  ) {
    return value
  }
  try {
    return JSON.parse(trimmed)
  } catch {
    return value
  }
}

function safeGetResult(raw: unknown): unknown {
  const value = tryParseJsonPayload(raw)

  if (value && typeof value === 'object' && 'result' in (value as Record<string, unknown>)) {
    return tryParseJsonPayload((value as { result?: unknown }).result)
  }

  return value
}

function asRecord(val: unknown): Record<string, unknown> | null {
  if (!val || typeof val !== 'object') return null
  return val as Record<string, unknown>
}

function asArray(val: unknown): unknown[] {
  if (!Array.isArray(val)) return []
  return val
}

function pickFirstString(
  obj: Record<string, unknown> | null,
  keys: string[]
): string | undefined {
  if (!obj) return undefined
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'string' && value.trim()) return value
  }
  return undefined
}

function pickFirstBoolean(
  obj: Record<string, unknown> | null,
  keys: string[]
): boolean | undefined {
  if (!obj) return undefined
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'boolean') return value
  }
  return undefined
}

function pickFirstNumber(
  obj: Record<string, unknown> | null,
  keys: string[]
): number | undefined {
  if (!obj) return undefined
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return undefined
}

function parseMaybeJsonRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value))
    } catch {
      return null
    }
  }
  return asRecord(value)
}

const SPACES_ENTITY_LABELS: Record<
  SpacesEntityType,
  { singular: string; plural: string }
> = {
  item: { singular: 'Losi item', plural: 'Losi items' },
  space: { singular: 'Space', plural: 'Spaces' },
  task: { singular: 'Task', plural: 'Tasks' },
  note: { singular: 'Note', plural: 'Notes' },
  folder: { singular: 'Folder', plural: 'Folders' },
  chat: { singular: 'Chat', plural: 'Chats' },
  conversation: { singular: 'Conversation', plural: 'Conversations' },
  member: { singular: 'Member', plural: 'Members' },
  whiteboard: { singular: 'Whiteboard', plural: 'Whiteboards' },
  design: { singular: 'Design', plural: 'Designs' },
  sheet: { singular: 'Sheet', plural: 'Sheets' },
  sprint: { singular: 'Sprint', plural: 'Sprints' },
  event: { singular: 'Event', plural: 'Events' },
  file: { singular: 'File', plural: 'Files' },
  collection: { singular: 'Collection', plural: 'Collections' },
  calendar: { singular: 'Calendar', plural: 'Calendars' }
}

const SPACES_ACTION_LABELS: Record<string, string> = {
  listSpaces: 'Spaces',
  createSpace: 'Space',
  updateSpace: 'Space',
  archiveSpace: 'Space',
  listSpaceTasks: 'Tasks',
  getSpaceTask: 'Task',
  createSpaceTask: 'Task',
  updateSpaceTask: 'Task',
  deleteSpaceTask: 'Task',
  listTaskSubtasks: 'Subtasks',
  createTaskSubtask: 'Subtask',
  updateTaskSubtask: 'Subtask',
  deleteTaskSubtask: 'Subtask',
  listTaskChecklistItems: 'Checklist',
  createTaskChecklistItem: 'Checklist item',
  updateTaskChecklistItem: 'Checklist item',
  deleteTaskChecklistItem: 'Checklist item',
  listTaskNotes: 'Task notes',
  deleteTaskNote: 'Task note',
  listTaskLinkedItems: 'Linked items',
  searchTaskAttachableItems: 'Attachable items',
  attachTaskLinkedItem: 'Linked item',
  removeTaskLinkedItem: 'Linked item',
  listSpaceNotes: 'Notes',
  getSpaceNote: 'Note',
  createSpaceNote: 'Note',
  updateSpaceNote: 'Note',
  deleteSpaceNote: 'Note',
  listSpaceFolders: 'Folders',
  getSpaceFolder: 'Folder',
  createSpaceFolder: 'Folder',
  updateSpaceFolder: 'Folder',
  listSpaceSheets: 'Sheets',
  getSpaceSheet: 'Sheet',
  createSpaceSheet: 'Sheet',
  updateSpaceSheet: 'Sheet',
  deleteSpaceSheet: 'Sheet',
  replaceSpaceSheetWorkbook: 'Sheet workbook',
  applySpaceSheetRangeUpdate: 'Sheet range',
  applySpaceSheetRangeFill: 'Sheet range',
  applySpaceSheetRangeTransfer: 'Sheet range transfer',
  applySpaceSheetStructureUpdate: 'Sheet structure',
  applySpaceSheetDimensionUpdate: 'Sheet dimensions',
  applySpaceSheetVisibilityUpdate: 'Sheet visibility',
  listSpaceSprints: 'Sprints',
  getSpaceSprint: 'Sprint',
  createSpaceSprint: 'Sprint',
  updateSpaceSprint: 'Sprint',
  listSpaceEvents: 'Events',
  getSpaceEvent: 'Event',
  createSpaceEvent: 'Event',
  updateSpaceEvent: 'Event',
  listManagedCalendars: 'Managed calendars',
  listManagedCalendarEvents: 'Managed calendar events',
  createManagedCalendarEvent: 'Managed event',
  updateManagedCalendarEvent: 'Managed event',
  deleteManagedCalendarEvent: 'Managed event',
  checkManagedCalendarAvailability: 'Calendar availability',
  listSpaceFiles: 'Files',
  getSpaceFile: 'File',
  searchSpaceFiles: 'Files',
  attachSpaceFile: 'File',
  updateSpaceFile: 'File',
  detachSpaceFile: 'File',
  listSpaceCollections: 'Collections',
  getSpaceCollection: 'Collection',
  attachSpaceCollection: 'Collection',
  updateSpaceCollection: 'Collection',
  detachSpaceCollection: 'Collection',
  listSpaceMembers: 'Members',
  listSpaceMemberDirectory: 'Member directory',
  addSpaceMember: 'Member',
  updateSpaceMemberRole: 'Member role',
  removeSpaceMember: 'Member',
  inviteToSpace: 'Space invitation',
  listSpaceChats: 'Chats',
  getSpaceChat: 'Chat link',
  linkSpaceChat: 'Chat link',
  moveSpaceChat: 'Chat link',
  unlinkSpaceChat: 'Chat link',
  listSpaceConversations: 'Conversations',
  startSpaceConversation: 'Conversation',
  getSpaceConversation: 'Conversation',
  updateSpaceConversation: 'Conversation',
  archiveSpaceConversation: 'Conversation',
  duplicateSpaceConversation: 'Conversation',
  listSpaceConversationMessages: 'Conversation messages',
  respondSpaceConversation: 'Conversation reply',
  listSpaceWhiteboards: 'Whiteboards',
  getSpaceWhiteboard: 'Whiteboard',
  createSpaceWhiteboard: 'Whiteboard',
  updateSpaceWhiteboard: 'Whiteboard',
  deleteSpaceWhiteboard: 'Whiteboard',
  listSpaceDesigns: 'Designs',
  getSpaceDesign: 'Design',
  createSpaceDesign: 'Design',
  updateSpaceDesign: 'Design',
  deleteSpaceDesign: 'Design',
  applySpaceDesignSceneMutation: 'Design update'
}

function getSpacesActionLabel(functionId: string): string {
  return SPACES_ACTION_LABELS[functionId] || functionId
}

function getSpacesCompletedActionLabel(functionId: string) {
  const label = getSpacesActionLabel(functionId)

  if (functionId.startsWith('list')) return `Listed ${label.toLowerCase()}`
  if (functionId.startsWith('search')) return `Searched ${label.toLowerCase()}`
  if (functionId.startsWith('create') || functionId.startsWith('start')) {
    return `Created ${label.toLowerCase()}`
  }
  if (functionId.startsWith('update') || functionId.startsWith('apply')) {
    return `Updated ${label.toLowerCase()}`
  }
  if (functionId.startsWith('delete')) return `Deleted ${label.toLowerCase()}`
  if (functionId.startsWith('replace')) return `Replaced ${label.toLowerCase()}`
  if (functionId.startsWith('archive')) return `Archived ${label.toLowerCase()}`
  if (functionId.startsWith('remove')) return `Removed ${label.toLowerCase()}`
  if (functionId.startsWith('unlink')) return `Unlinked ${label.toLowerCase()}`
  if (functionId.startsWith('detach')) return `Detached ${label.toLowerCase()}`
  if (functionId.startsWith('move')) return `Moved ${label.toLowerCase()}`
  if (functionId.startsWith('duplicate')) return `Duplicated ${label.toLowerCase()}`
  if (functionId.startsWith('invite')) return `Invited ${label.toLowerCase()}`
  if (functionId.startsWith('respond')) return `Replied in ${label.toLowerCase()}`
  if (functionId.startsWith('attach')) return `Attached ${label.toLowerCase()}`
  if (functionId.startsWith('link')) return `Linked ${label.toLowerCase()}`
  if (functionId.startsWith('check')) return `Checked ${label.toLowerCase()}`
  if (functionId.startsWith('get')) return `Opened ${label.toLowerCase()}`

  return `${label} updated`
}

// --------------

function normalizeWebSearch(key: string, rawResult: unknown): WebSearchNormalizedResult {
  // Caller already ran safeGetResult (JSON string + `{ result }` unwrap).
  const base: WebSearchNormalizedResult = {
    kind: 'web_search',
    key,
    label: 'Web search result',
    query: null,
    raw: rawResult
  }

  const data = asRecord(rawResult)
  if (!data) return base

  const query =
    pickFirstString(data, ['query']) ||
    (asRecord(data.searchParameters)?.q as string | undefined) ||
    null

  const organic = asArray(data.organic)
  const searchInfo = asRecord(data.searchInformation)
  const totalResultsRaw = data.totalResults ?? searchInfo?.totalResults
  const totalResults =
    typeof totalResultsRaw === 'number'
      ? totalResultsRaw
      : typeof totalResultsRaw === 'string'
        ? Number.parseInt(totalResultsRaw, 10) || undefined
        : undefined

  return {
    ...base,
    label: query ? `Searched "${query}"` : base.label,
    query,
    url: typeof data.url === 'string' ? data.url : undefined,
    totalResults: organic.length || totalResults
  }
}

function normalizeFetchedUrl(key: string, rawResult: unknown): FetchedUrlNormalizedResult {
  const data = asRecord(rawResult) ?? {}
  const url = pickFirstString(data, ['url', 'href']) ?? ''

  const markdown =
    pickFirstString(data, ['mdDoc', 'markdown', 'content']) ??
    pickFirstString(data, [
      'body',
      'bodyMarkdown',
      'bodyMd',
      'html',
      'htmlContent'
    ])

  return {
    kind: 'fetched_url',
    key,
    label: url || 'Fetched URL',
    url,
    title: pickFirstString(data, ['title', 'pageTitle']),
    markdown
  }
}

/** Compact WMO weather_code labels (Open-Meteo). */
function wmoWeatherCodeLabel(code: number): string | undefined {
  const labels: Record<number, string> = {
    0: 'Clear sky',
    1: 'Mainly clear',
    2: 'Partly cloudy',
    3: 'Overcast',
    45: 'Fog',
    48: 'Depositing rime fog',
    51: 'Light drizzle',
    53: 'Moderate drizzle',
    55: 'Dense drizzle',
    61: 'Slight rain',
    63: 'Moderate rain',
    65: 'Heavy rain',
    71: 'Slight snow',
    73: 'Moderate snow',
    75: 'Heavy snow',
    80: 'Slight rain showers',
    81: 'Moderate rain showers',
    82: 'Violent rain showers',
    95: 'Thunderstorm',
    96: 'Thunderstorm with slight hail',
    99: 'Thunderstorm with heavy hail'
  }
  return labels[code]
}

function normalizeWeather(key: string, rawResult: unknown): WeatherNormalizedResult {
  const data = asRecord(rawResult)
  const current = asRecord(data?.current)

  const location =
    pickFirstString(data, ['location', 'city', 'timezone']) ??
    (asRecord(data?.location)?.name as string | undefined)

  const weatherCodeRaw = current?.weather_code ?? data?.weather_code
  const weatherCode =
    typeof weatherCodeRaw === 'number'
      ? weatherCodeRaw
      : typeof weatherCodeRaw === 'string'
        ? Number.parseInt(weatherCodeRaw, 10)
        : undefined

  const summary =
    pickFirstString(data, ['summary', 'condition']) ??
    (typeof current?.condition === 'string' ? current.condition : undefined) ??
    (typeof weatherCode === 'number' && Number.isFinite(weatherCode)
      ? wmoWeatherCodeLabel(weatherCode)
      : undefined)

  let temperatureC: number | undefined
  let temperatureF: number | undefined

  if (data) {
    // Open-Meteo current.temperature_2m is °C; also accept legacy temp_c/f shapes.
    const openMeteoC = current?.temperature_2m
    const c =
      data.temperature_c ??
      current?.temp_c ??
      (typeof openMeteoC === 'number' ? openMeteoC : undefined)
    const f = data.temperature_f ?? current?.temp_f
    if (typeof c === 'number' && Number.isFinite(c)) temperatureC = c
    if (typeof f === 'number' && Number.isFinite(f)) temperatureF = f
    if (typeof temperatureC === 'number' && typeof temperatureF !== 'number') {
      temperatureF = (temperatureC * 9) / 5 + 32
    }
  }

  return {
    kind: 'weather',
    key,
    label: location || 'Weather',
    location,
    summary,
    temperatureC,
    temperatureF,
    raw: rawResult
  }
}

function normalizeFinanceQuote(
  key: string,
  rawResult: unknown
): FinanceQuoteNormalizedResult {
  const data = asRecord(rawResult)
  const quote =
    asRecord(asArray(data?.quotes)[0]) ||
    asRecord(data?.quote) ||
    data

  const symbol = pickFirstString(quote, ['symbol', 'ticker'])
  const name = pickFirstString(quote, ['name', 'shortName', 'longName'])

  const priceRaw =
    quote?.regularMarketPrice ??
    quote?.price ??
    quote?.lastPrice

  const price =
    typeof priceRaw === 'number'
      ? priceRaw
      : typeof priceRaw === 'string'
        ? Number.parseFloat(priceRaw)
        : undefined

  const changeRaw = quote?.regularMarketChange ?? quote?.change
  const change =
    typeof changeRaw === 'number'
      ? changeRaw
      : typeof changeRaw === 'string'
        ? Number.parseFloat(changeRaw)
        : undefined

  const changePercentRaw =
    quote?.regularMarketChangePercent ??
    quote?.changePercent

  const changePercent =
    typeof changePercentRaw === 'number'
      ? changePercentRaw
      : typeof changePercentRaw === 'string'
        ? Number.parseFloat(changePercentRaw)
        : undefined

  const currency = pickFirstString(quote, ['currency'])

  const label = symbol ? symbol : name || 'Finance quote'

  return {
    kind: 'finance_quote',
    key,
    label,
    symbol,
    name,
    price,
    change,
    changePercent,
    currency,
    raw: rawResult
  }
}

function normalizeVirtualFile(
  key: string,
  rawResult: unknown
): VirtualFileSingleNormalizedResult | VirtualFileListNormalizedResult | null {
  const data = asRecord(rawResult)
  if (!data) return null

  if (Array.isArray(data.files)) {
    const files: Array<{ id: string; path: string; name: string }> = []
    for (const file of data.files as Array<Record<string, unknown>>) {
      if (!file || typeof file !== 'object') continue
      if (typeof file.id !== 'string' || typeof file.path !== 'string') continue
      files.push({
        id: file.id as string,
        path: file.path as string,
        name:
          (file.name as string) ??
          (file.path as string).split('/').pop() ??
          (file.path as string)
      })
    }
    if (files.length === 0) return null
    return {
      kind: 'virtual_file_list',
      key,
      label: 'Virtual files',
      files
    }
  }

  if (typeof data.id === 'string' && typeof data.path === 'string') {
    return {
      kind: 'virtual_file_single',
      key,
      label:
        (data.name as string) ??
        (data.path as string).split('/').pop() ??
        (data.path as string),
      file: {
        id: data.id as string,
        path: data.path as string,
        name:
          (data.name as string) ??
          (data.path as string).split('/').pop() ??
          (data.path as string),
        content: typeof data.content === 'string' ? (data.content as string) : undefined
      }
    }
  }

  return null
}

function normalizeTasks(key: string, rawResult: unknown): TaskNormalizedResult | null {
  const data = asRecord(rawResult)
  if (!data) return null

  const tasksRaw =
    asArray(data.tasks) ||
    (Array.isArray(data.items) ? (data.items as unknown[]) : [])

  const tasks = tasksRaw.map((t) => {
    const rec = asRecord(t)
    if (!rec) return {}
    return {
      id: typeof rec.id === 'string' ? (rec.id as string) : undefined,
      title:
        pickFirstString(rec, ['title', 'name', 'summary']) ??
        undefined,
      status: pickFirstString(rec, ['status', 'state']) ?? undefined,
      due_date:
        (rec.due_date as string | null | undefined) ??
        (rec.dueDate as string | null | undefined) ??
        null,
      created_at:
        (rec.created_at as string | null | undefined) ??
        (rec.createdAt as string | null | undefined) ??
        null
    }
  })

  const filtered = tasks.filter(
    (t) => t.id || t.title || t.status || t.due_date || t.created_at
  )

  if (filtered.length === 0) return null

  const kind: TaskNormalizedResult['kind'] =
    filtered.length === 1 ? 'task' : 'task_list'

  const firstTitle = filtered[0]?.title

  return {
    kind,
    key,
    label: firstTitle || 'Tasks',
    tasks: filtered
  }
}

function buildSpacesStatus(
  key: string,
  functionId: string,
  state: SpacesStatusNormalizedResult['state'],
  title: string,
  description?: string,
  detailLines?: string[]
): SpacesStatusNormalizedResult {
  return {
    kind: 'spaces_status',
    key,
    label: getSpacesActionLabel(functionId),
    state,
    title,
    description,
    detailLines
  }
}

function normalizeSpacesEntitySummary(
  type: SpacesEntityType,
  value: unknown
): SpacesEntitySummary | null {
  const rec = asRecord(value)
  if (!rec) return null

  if (type === 'member') {
    const title =
      pickFirstString(rec, ['display_name', 'username', 'email', 'user_id']) ||
      'Space member'

    return {
      id: pickFirstString(rec, ['user_id', 'id']),
      type,
      title,
      description: pickFirstString(rec, ['email']),
      role: pickFirstString(rec, ['role', 'workspace_role']),
      email: pickFirstString(rec, ['email']),
      username: pickFirstString(rec, ['username']),
      imageUrl: pickFirstString(rec, ['image_url']),
      updatedAt: pickFirstString(rec, ['last_viewed_at']),
      createdAt: pickFirstString(rec, ['added_at'])
    }
  }

  if (type === 'chat') {
    const linkedChat = asRecord(rec.chats)

    return {
      id: pickFirstString(rec, ['chat_id', 'chatId', 'id']),
      type,
      title:
        pickFirstString(linkedChat, ['name']) ||
        pickFirstString(rec, ['name', 'chat_id']) ||
        'Linked chat',
      description:
        pickFirstString(rec, ['description']) ||
        pickFirstString(linkedChat, ['model']),
      model: pickFirstString(linkedChat, ['model']),
      contextType: pickFirstString(rec, ['context_type']),
      url: pickFirstString(rec, ['url', 'href']),
      updatedAt:
        pickFirstString(linkedChat, ['updated_at', 'updatedAt', 'created_at']) ||
        pickFirstString(rec, ['updated_at', 'updatedAt', 'created_at']),
      createdAt:
        pickFirstString(linkedChat, ['created_at', 'createdAt']) ||
        pickFirstString(rec, ['created_at', 'createdAt']),
      archived: pickFirstBoolean(rec, ['archived'])
    }
  }

  const fileMetadata = type === 'file' ? asRecord(rec.files) : null
  const collectionMetadata = type === 'collection' ? asRecord(rec.collections) : null
  const title =
    type === 'file'
      ? pickFirstString(fileMetadata, ['name']) ||
        pickFirstString(rec, ['title', 'name', 'file_name', 'filename', 'path', 'file_id'])
      : type === 'collection'
        ? pickFirstString(collectionMetadata, ['name']) ||
          pickFirstString(rec, ['title', 'name', 'collection_name', 'collection_id'])
        : type === 'calendar'
          ? pickFirstString(rec, ['title', 'name', 'calendar_name', 'calendar_id'])
          : type === 'space' || type === 'folder' || type === 'conversation' || type === 'whiteboard'
            ? pickFirstString(rec, ['name', 'title'])
            : pickFirstString(rec, ['title', 'name'])

  const description =
    type === 'note'
      ? pickFirstString(rec, ['content', 'description'])
      : type === 'file'
        ? pickFirstString(fileMetadata, ['description', 'type']) ||
          pickFirstString(rec, ['description', 'content_type', 'mime_type', 'type'])
        : type === 'collection'
          ? pickFirstString(collectionMetadata, ['description']) ||
            pickFirstString(rec, ['description', 'content'])
        : pickFirstString(rec, ['description', 'content'])

  const summary: SpacesEntitySummary = {
    id:
      type === 'file'
        ? pickFirstString(rec, ['file_id', 'fileId', 'id'])
        : type === 'collection'
          ? pickFirstString(rec, ['collection_id', 'collectionId', 'id'])
          : pickFirstString(rec, [
              'id',
              'file_id',
              'fileId',
              'collection_id',
              'collectionId',
              'calendar_id',
              'calendarId'
            ]),
    type,
    title: title || SPACES_ENTITY_LABELS[type].singular,
    description,
    url: pickFirstString(rec, ['url', 'href']),
    status: type === 'task' || type === 'sprint' || type === 'event'
      ? pickFirstString(rec, ['status', 'state'])
      : undefined,
    priority: type === 'task' ? pickFirstString(rec, ['priority']) : undefined,
    role: undefined,
    sharing: pickFirstString(rec, ['sharing']),
    model: type === 'conversation' ? pickFirstString(rec, ['model']) : undefined,
    contextType: type === 'conversation' ? pickFirstString(rec, ['context_type']) : undefined,
    email: undefined,
    username: undefined,
    imageUrl: pickFirstString(rec, ['image_url']),
    dueDate: type === 'task' ? pickFirstString(rec, ['due_date', 'dueDate']) ?? null : null,
    updatedAt: pickFirstString(rec, ['updated_at', 'updatedAt']),
    createdAt: pickFirstString(rec, ['created_at', 'createdAt', 'attached_at']),
    locked: pickFirstBoolean(rec, ['locked']),
    archived:
      pickFirstBoolean(rec, ['archived']) ??
      (pickFirstString(rec, ['archived_at']) ? true : undefined),
    favorited: pickFirstBoolean(rec, ['favorited'])
  }

  if (!summary.id && !summary.title && !summary.description) return null

  return summary
}

const TASK_LINKED_ENTITY_TYPES = new Set<SpacesEntityType>([
  'chat',
  'conversation',
  'design',
  'file',
  'folder',
  'note',
  'collection',
  'calendar',
  'sheet',
  'sprint',
  'task',
  'whiteboard'
])

function normalizeTaskLinkedEntityType(value: unknown): SpacesEntityType {
  if (typeof value !== 'string') {
    return 'item'
  }

  return TASK_LINKED_ENTITY_TYPES.has(value as SpacesEntityType)
    ? (value as SpacesEntityType)
    : 'item'
}

function normalizeTaskLinkedEntitySummary(value: unknown): SpacesEntitySummary | null {
  const rec = asRecord(value)
  if (!rec) return null

  const metadata = asRecord(rec.metadata)
  const type = normalizeTaskLinkedEntityType(
    pickFirstString(rec, ['item_type', 'itemType', 'type'])
  )
  const id = pickFirstString(rec, ['item_id', 'itemId', 'id'])
  const title = pickFirstString(rec, ['title', 'name'])
  const description = pickFirstString(rec, ['subtitle', 'description', 'content'])
  const url = pickFirstString(rec, ['item_url', 'url', 'href'])

  if (!id && !title && !description && !url) return null

  return {
    id,
    type,
    title: title || SPACES_ENTITY_LABELS[type].singular,
    description,
    url,
    status:
      pickFirstString(metadata, ['status', 'state']) ||
      pickFirstString(rec, ['status', 'state']) ||
      undefined,
    priority:
      pickFirstString(metadata, ['priority']) ||
      pickFirstString(rec, ['priority']) ||
      undefined,
    role: undefined,
    sharing: undefined,
    model:
      type === 'chat'
        ? pickFirstString(metadata, ['model']) || pickFirstString(rec, ['model']) || undefined
        : undefined,
    contextType:
      type === 'conversation'
        ? pickFirstString(metadata, ['context_type', 'contextType']) ||
          pickFirstString(rec, ['context_type', 'contextType']) ||
          undefined
        : undefined,
    email: undefined,
    username: undefined,
    imageUrl: undefined,
    dueDate:
      type === 'task'
        ? pickFirstString(metadata, ['due_date', 'dueDate']) ||
          pickFirstString(rec, ['due_date', 'dueDate']) ||
          null
        : null,
    updatedAt: pickFirstString(rec, ['updated_at', 'updatedAt']),
    createdAt: pickFirstString(rec, ['created_at', 'createdAt']),
    locked: undefined,
    archived:
      pickFirstBoolean(rec, ['archived']) ??
      (pickFirstString(rec, ['archived_at']) ? true : undefined),
    favorited: undefined
  }
}

function normalizeSpacesEntityResult(
  key: string,
  functionId: string,
  data: Record<string, unknown>
): SpacesEntityNormalizedResult | SpacesStatusNormalizedResult | null {
  const mappings: Array<{
    singularKey: string
    pluralKey: string
    entityType: SpacesEntityType
  }> = [
    { singularKey: 'space', pluralKey: 'spaces', entityType: 'space' },
    { singularKey: 'task', pluralKey: 'tasks', entityType: 'task' },
    { singularKey: 'note', pluralKey: 'notes', entityType: 'note' },
    { singularKey: 'folder', pluralKey: 'folders', entityType: 'folder' },
    { singularKey: 'chatLink', pluralKey: 'chats', entityType: 'chat' },
    {
      singularKey: 'conversation',
      pluralKey: 'conversations',
      entityType: 'conversation'
    },
    { singularKey: 'whiteboard', pluralKey: 'whiteboards', entityType: 'whiteboard' },
    { singularKey: 'design', pluralKey: 'designs', entityType: 'design' },
    { singularKey: 'sheet', pluralKey: 'sheets', entityType: 'sheet' },
    { singularKey: 'sprint', pluralKey: 'sprints', entityType: 'sprint' },
    { singularKey: 'event', pluralKey: 'events', entityType: 'event' },
    { singularKey: 'member', pluralKey: 'members', entityType: 'member' },
    { singularKey: 'calendarEvent', pluralKey: 'calendarEvents', entityType: 'event' },
    { singularKey: 'managedCalendarEvent', pluralKey: 'managedCalendarEvents', entityType: 'event' },
    { singularKey: 'file', pluralKey: 'files', entityType: 'file' },
    { singularKey: 'collection', pluralKey: 'collections', entityType: 'collection' },
    { singularKey: 'calendar', pluralKey: 'calendars', entityType: 'calendar' },
    { singularKey: 'managedCalendar', pluralKey: 'managedCalendars', entityType: 'calendar' }
  ]

  for (const mapping of mappings) {
    if (mapping.singularKey in data) {
      const item = normalizeSpacesEntitySummary(mapping.entityType, data[mapping.singularKey])
      if (!item) {
        return buildSpacesStatus(
          key,
          functionId,
          'empty',
          `No ${SPACES_ENTITY_LABELS[mapping.entityType].singular.toLowerCase()} available`
        )
      }

      return {
        kind: 'spaces_entity',
        key,
        label: getSpacesCompletedActionLabel(functionId),
        entityType: mapping.entityType,
        items: [item]
      }
    }

    if (mapping.pluralKey in data) {
      const items = asArray(data[mapping.pluralKey])
        .map((item) => normalizeSpacesEntitySummary(mapping.entityType, item))
        .filter((item): item is SpacesEntitySummary => item != null)

      if (items.length === 0) {
        return buildSpacesStatus(
          key,
          functionId,
          'empty',
          `No ${SPACES_ENTITY_LABELS[mapping.entityType].plural.toLowerCase()} found`
        )
      }

      return {
        kind: 'spaces_entity_list',
        key,
        label: getSpacesCompletedActionLabel(functionId),
        entityType: mapping.entityType,
        items
      }
    }
  }

  return null
}

function normalizeSpacesTaskSupport(
  key: string,
  functionId: string,
  data: Record<string, unknown>
): SpacesTaskSupportNormalizedResult | SpacesStatusNormalizedResult | null {
  if ('subtasks' in data || 'subtask' in data) {
    const items = (
      'subtasks' in data ? asArray(data.subtasks) : [data.subtask]
    ).map((item) => {
      const rec = asRecord(item)
      if (!rec) return null
      return {
        id: pickFirstString(rec, ['id']),
        title: pickFirstString(rec, ['title', 'name']),
        description: pickFirstString(rec, ['description']),
        completed: pickFirstBoolean(rec, ['completed']),
        dueDate: pickFirstString(rec, ['due_date', 'dueDate']) ?? null
      }
    }).filter((item): item is NonNullable<typeof item> => item != null)

    if (items.length === 0) {
      return buildSpacesStatus(key, functionId, 'empty', 'No subtasks found')
    }

    return {
      kind: 'spaces_task_support',
      key,
      label: getSpacesCompletedActionLabel(functionId),
      supportType: 'subtasks',
      taskId: pickFirstString(data, ['taskId']),
      items
    }
  }

  if ('checklistItems' in data || 'checklistItem' in data) {
    const items = (
      'checklistItems' in data ? asArray(data.checklistItems) : [data.checklistItem]
    ).map((item) => {
      const rec = asRecord(item)
      if (!rec) return null
      return {
        id: pickFirstString(rec, ['id']),
        title: pickFirstString(rec, ['text', 'title', 'name']),
        description: undefined,
        completed: pickFirstBoolean(rec, ['completed']),
        dueDate: null
      }
    }).filter((item): item is NonNullable<typeof item> => item != null)

    if (items.length === 0) {
      return buildSpacesStatus(key, functionId, 'empty', 'No checklist items found')
    }

    return {
      kind: 'spaces_task_support',
      key,
      label: getSpacesCompletedActionLabel(functionId),
      supportType: 'checklist',
      taskId: pickFirstString(data, ['taskId']),
      items
    }
  }

  return null
}

function normalizeSpacesConversationMessages(
  key: string,
  functionId: string,
  data: Record<string, unknown>
): SpacesConversationMessagesNormalizedResult | SpacesStatusNormalizedResult | null {
  if (
    !('messages' in data) &&
    !('assistantMessage' in data) &&
    !('userMessage' in data)
  ) {
    return null
  }

  const messagesRaw = 'messages' in data
    ? asArray(data.messages)
    : [data.userMessage, data.assistantMessage].filter(Boolean)

  const messages = messagesRaw.map((message) => {
    const rec = asRecord(message)
    if (!rec) return null
    const author = asRecord(rec.author)
    return {
      id: pickFirstString(rec, ['id']),
      role: pickFirstString(rec, ['role']),
      content: pickFirstString(rec, ['content']),
      authorName:
        pickFirstString(author, ['display_name', 'username']) ||
        pickFirstString(author, ['email']) ||
        undefined,
      errorMessage: pickFirstString(rec, ['error_message']),
      model: pickFirstString(rec, ['model']),
      createdAt: pickFirstString(rec, ['created_at', 'createdAt']) ?? null
    }
  }).filter((message): message is NonNullable<typeof message> => message != null)

  if (messages.length === 0) {
    return buildSpacesStatus(key, functionId, 'empty', 'No conversation messages found')
  }

  const conversation = 'conversation' in data
    ? normalizeSpacesEntitySummary('conversation', data.conversation)
    : undefined
  const label = functionId === 'getSpaceChat'
    ? 'Chat messages'
    : conversation?.title || 'Conversation messages'

  return {
    kind: 'spaces_conversation_messages',
    key,
    label,
    conversation: conversation || undefined,
    messages
  }
}

function normalizeSpacesInvitation(
  key: string,
  functionId: string,
  data: Record<string, unknown>
): SpacesInvitationNormalizedResult | null {
  if (!('invitation' in data)) return null

  const invitation = asRecord(data.invitation)
  const details = asRecord(invitation?.details)

  return {
    kind: 'spaces_invitation',
    key,
    label: 'Space invitation',
    message: pickFirstString(invitation, ['message']),
    createdCount: pickFirstNumber(details, ['created']),
    requestedCount: pickFirstNumber(details, ['totalRequested']),
    emailsSent: asArray(invitation?.emailsSent).filter(
      (value): value is string => typeof value === 'string'
    ),
    emailsFailed: asArray(invitation?.emailsFailed).filter(
      (value): value is string => typeof value === 'string'
    ),
    validationErrors: asArray(details?.validationErrors).filter(
      (value): value is string => typeof value === 'string'
    )
  }
}

function normalizeLosiSpaces(
  key: string,
  rawResult: unknown
):
  | SpacesEntityNormalizedResult
  | SpacesTaskSupportNormalizedResult
  | SpacesConversationMessagesNormalizedResult
  | SpacesInvitationNormalizedResult
  | SpacesStatusNormalizedResult
  | null {
  if (!key.startsWith('losiSpaces__')) return null

  if (rawResult == null) {
    const functionId = key.slice('losiSpaces__'.length)
    return buildSpacesStatus(
      key,
      functionId,
      'loading',
      `Loading ${getSpacesActionLabel(functionId).toLowerCase()}`
    )
  }

  const data = parseMaybeJsonRecord(rawResult)
  const routedFunctionId =
    typeof data?.routedFunctionId === 'string' && data.routedFunctionId.trim()
      ? data.routedFunctionId.trim()
      : null
  const functionId = routedFunctionId ?? key.slice('losiSpaces__'.length)
  if (!data) {
    return buildSpacesStatus(
      key,
      functionId,
      'success',
      `${getSpacesActionLabel(functionId)} ready`
    )
  }

  if (data.success === false) {
    return buildSpacesStatus(
      key,
      functionId,
      'error',
      `${getSpacesActionLabel(functionId)} failed`,
      pickFirstString(data, ['error']) || 'The Spaces action did not complete.'
    )
  }

  if (
    functionId === 'searchTaskAttachableItems' ||
    'linkedItems' in data ||
    'linkedItem' in data
  ) {
    const rawItems =
      'linkedItems' in data
        ? asArray(data.linkedItems)
        : 'linkedItem' in data
          ? [data.linkedItem]
          : asArray(data.items)
    const items = rawItems
      .map((item) => normalizeTaskLinkedEntitySummary(item))
      .filter((item): item is SpacesEntitySummary => item != null)

    if (items.length === 0) {
      const emptyMessage =
        functionId === 'searchTaskAttachableItems'
          ? 'No attachable Losi items found'
          : 'No linked Losi items found'

      return buildSpacesStatus(key, functionId, 'empty', emptyMessage)
    }

    const entityTypes = new Set(items.map((item) => item.type))
    const entityType = entityTypes.size === 1 ? items[0].type : 'item'

    if ('linkedItem' in data && !('linkedItems' in data)) {
      return {
        kind: 'spaces_entity',
        key,
        label: getSpacesCompletedActionLabel(functionId),
        entityType,
        items
      }
    }

    return {
      kind: 'spaces_entity_list',
      key,
      label: getSpacesCompletedActionLabel(functionId),
      entityType,
      items
    }
  }

  const taskSupport = normalizeSpacesTaskSupport(key, functionId, data)
  if (taskSupport) return taskSupport

  const invitation = normalizeSpacesInvitation(key, functionId, data)
  if (invitation) return invitation

  const messages = normalizeSpacesConversationMessages(key, functionId, data)
  if (messages) return messages

  if ('workspaceCandidates' in data) {
    const candidates = asArray(data.workspaceCandidates)
      .map((candidate) => normalizeSpacesEntitySummary('member', candidate))
      .filter((candidate): candidate is SpacesEntitySummary => candidate != null)

    if (candidates.length > 0) {
      return {
        kind: 'spaces_entity_list',
        key,
        label: getSpacesCompletedActionLabel(functionId),
        entityType: 'member',
        items: candidates
      }
    }

    if (!('members' in data)) {
      return buildSpacesStatus(key, functionId, 'empty', 'No workspace members found')
    }
  }

  const entityResult = normalizeSpacesEntityResult(key, functionId, data)
  if (entityResult) return entityResult

  if ('members' in data) {
    const items = asArray(data.members)
      .map((member) => normalizeSpacesEntitySummary('member', member))
      .filter((member): member is SpacesEntitySummary => member != null)

    if (items.length === 0) {
      return buildSpacesStatus(key, functionId, 'empty', 'No Space members found')
    }

    return {
      kind: 'spaces_entity_list',
      key,
      label: getSpacesCompletedActionLabel(functionId),
      entityType: 'member',
      items
    }
  }

  const detailLines: string[] = []
  if (typeof data.role === 'string') {
    detailLines.push(`Role: ${data.role}`)
  }
  if (typeof data.chatId === 'string') {
    detailLines.push(`Chat: ${data.chatId}`)
  }
  if (typeof data.userId === 'string') {
    detailLines.push(`User: ${data.userId}`)
  }

  const title = getSpacesCompletedActionLabel(functionId)

  return buildSpacesStatus(
    key,
    functionId,
    'success',
    title,
    undefined,
    detailLines.length > 0 ? detailLines : undefined
  )
}

function normalizeMemory(key: string, rawResult: unknown): MemoryNormalizedResult {
  const data = asRecord(rawResult)
  const itemsArray = asArray(data?.memories || data?.items)
  const items = itemsArray.map((m) => {
    const rec = asRecord(m)
    if (!rec) return {}
    return {
      id: typeof rec.id === 'string' ? (rec.id as string) : undefined,
      key: pickFirstString(rec, ['key', 'name']),
      value: pickFirstString(rec, ['value', 'content', 'text']),
      created_at:
        (rec.created_at as string | undefined) ??
        (rec.createdAt as string | undefined)
    }
  })

  const action: MemoryNormalizedResult['action'] =
    key === 'MemoryTool__storeMemoryToRemember'
      ? 'store'
      : key === 'MemoryTool__listMemories'
        ? 'list'
        : key === 'MemoryTool__updateMemory'
          ? 'update'
          : key === 'MemoryTool__deleteMemory'
            ? 'delete'
            : 'unknown'

  return {
    kind: 'memory',
    key,
    label: 'Memories',
    action,
    items,
    raw: rawResult
  }
}

function normalizeChatManagement(
  key: string,
  rawResult: unknown
): ChatManagementNormalizedResult {
  const data = asRecord(rawResult)
  const base: ChatManagementNormalizedResult = {
    kind: 'chat_management',
    key,
    label: 'Chat management',
    action: 'unknown',
    raw: rawResult
  }

  if (!data) return base

  if ('chats' in data) {
    const chats = asArray(data.chats).map((c) => {
      const rec = asRecord(c)
      if (!rec) return {}
      return {
        id: typeof rec.id === 'string' ? (rec.id as string) : undefined,
        name: pickFirstString(rec, ['name']),
        pinned: typeof rec.pinned === 'boolean' ? (rec.pinned as boolean) : undefined,
        model: pickFirstString(rec, ['model']),
        created_at:
          (rec.created_at as string | undefined) ??
          (rec.createdAt as string | undefined),
        updated_at:
          (rec.updated_at as string | undefined) ??
          (rec.updatedAt as string | undefined) ??
          null
      }
    })

    return {
      ...base,
      action: 'get_chats',
      label: 'Chats',
      chats
    }
  }

  if ('messages' in data) {
    const messages = asArray(data.messages).map((m) => {
      const rec = asRecord(m)
      if (!rec) return {}
      return {
        id: typeof rec.id === 'string' ? (rec.id as string) : undefined,
        content: pickFirstString(rec, ['content']),
        role: pickFirstString(rec, ['role']),
        thumbs_up:
          typeof rec.thumbs_up === 'boolean' || rec.thumbs_up === null
            ? (rec.thumbs_up as boolean | null)
            : undefined,
        created_at:
          (rec.created_at as string | undefined) ??
          (rec.createdAt as string | undefined)
      }
    })

    return {
      ...base,
      action: 'get_messages',
      label: 'Chat messages',
      messages
    }
  }

  if ('chat' in data) {
    const chat = asRecord(data.chat)
    const pinned =
      typeof data.pinned === 'boolean'
        ? (data.pinned as boolean)
        : typeof chat?.pinned === 'boolean'
          ? (chat?.pinned as boolean)
          : undefined
    return {
      ...base,
      action: 'pin_chat',
      label: pinned ? 'Pinned chat' : 'Updated chat pin',
      chats: chat
        ? [
            {
              id: typeof chat.id === 'string' ? (chat.id as string) : undefined,
              name: pickFirstString(chat, ['name']),
              pinned
            }
          ]
        : undefined
    }
  }

  if ('messageId' in data || 'thumbs_up' in data) {
    return {
      ...base,
      action: 'star_message',
      label: 'Updated message feedback',
      messages: [
        {
          id:
            typeof data.messageId === 'string'
              ? (data.messageId as string)
              : undefined,
          thumbs_up:
            typeof data.thumbs_up === 'boolean' || data.thumbs_up === null
              ? (data.thumbs_up as boolean | null)
              : undefined
        }
      ]
    }
  }

  return base
}

function normalizeConnectedServices(
  key: string,
  rawResult: unknown
): ConnectedServicesNormalizedResult {
  const data = asRecord(rawResult)
  const base: ConnectedServicesNormalizedResult = {
    kind: 'connected_services',
    key,
    label: 'Connected services',
    operation: 'unknown',
    success: undefined,
    error: undefined,
    timestamp: undefined,
    extensions: undefined,
    actions: undefined,
    raw: rawResult
  }

  if (!data) return base

  const opRaw = pickFirstString(data, ['operation'])
  const keyImpliesCheck =
    key.includes('checkToolkitConnection') || key.includes('checkToolkit')
  const operation =
    opRaw === 'listExtensions'
      ? 'listExtensions'
      : opRaw === 'listActions'
        ? 'listActions'
        : opRaw === 'executeAction'
          ? 'executeAction'
          : opRaw === 'checkToolkit' ||
              (keyImpliesCheck &&
                (typeof data.connected === 'boolean' ||
                  typeof data.toolkit_slug === 'string' ||
                  typeof data.toolkitSlug === 'string'))
            ? 'checkToolkit'
            : 'unknown'

  const success =
    typeof data.success === 'boolean' ? (data.success as boolean) : undefined
  const error = pickFirstString(data, ['error', 'message'])
  const timestamp = pickFirstString(data, ['timestamp'])
  const connected =
    typeof data.connected === 'boolean' ? (data.connected as boolean) : undefined
  const toolkitSlug =
    pickFirstString(data, ['toolkit_slug', 'toolkitSlug']) ?? undefined
  const connectionUrl =
    pickFirstString(data, ['connection_url', 'connectionUrl']) ?? undefined
  const summary = pickFirstString(data, ['summary']) ?? undefined

  let setupCard: ConnectedServicesNormalizedResult['setupCard']
  const setupRaw = asRecord(data.setupCard) ?? asRecord(data.setup_card)
  if (setupRaw) {
    const slug =
      pickFirstString(setupRaw, ['toolkit_slug', 'toolkitSlug']) ?? toolkitSlug
    if (slug) {
      setupCard = {
        kind: 'composio_auth',
        title: pickFirstString(setupRaw, ['title']) || `Connect ${slug}`,
        description:
          pickFirstString(setupRaw, ['description']) ||
          'Connect this account so Losi can use it.',
        toolkit_slug: slug,
        connection_url:
          pickFirstString(setupRaw, ['connection_url', 'connectionUrl']) ||
          connectionUrl ||
          null,
        action_label:
          pickFirstString(setupRaw, ['action_label', 'actionLabel']) || 'Connect',
        logo: pickFirstString(setupRaw, ['logo']) || undefined
      }
    }
  } else if (operation === 'checkToolkit' && connected === false && toolkitSlug) {
    setupCard = {
      kind: 'composio_auth',
      title: `Connect ${toolkitSlug}`,
      description: summary || 'Connect this account so Losi can use it.',
      toolkit_slug: toolkitSlug,
      connection_url: connectionUrl || null,
      action_label: 'Connect'
    }
  }

  let extensions: ConnectedServicesNormalizedResult['extensions']
  let actions: ConnectedServicesNormalizedResult['actions']

  if (operation === 'listExtensions' && Array.isArray(data.data)) {
    extensions = data.data.map((ext) => {
      const rec = asRecord(ext)
      if (!rec) return {}
      return {
        name:
          pickFirstString(rec, ['extension_name', 'name']) ?? undefined,
        active:
          typeof rec.is_active === 'boolean'
            ? (rec.is_active as boolean)
            : undefined
      }
    })
  }

  if (operation === 'listActions' && Array.isArray(data.data)) {
    actions = data.data.map((act) => {
      const rec = asRecord(act)
      if (!rec) return {}
      return {
        id: pickFirstString(rec, ['id']),
        name: pickFirstString(rec, ['name']),
        description: pickFirstString(rec, ['description'])
      }
    })
  }

  return {
    ...base,
    operation,
    success,
    error,
    timestamp,
    connected,
    toolkitSlug,
    connectionUrl,
    summary,
    setupCard,
    extensions,
    actions
  }
}

function normalizeToolManagement(
  key: string,
  rawResult: unknown
): ToolManagementNormalizedResult {
  const data = asRecord(rawResult)
  const base: ToolManagementNormalizedResult = {
    kind: 'tool_management',
    key,
    label: 'Managed tools',
    action: 'unknown',
    raw: rawResult
  }

  if (!data) return base

  if ('tools' in data) {
    const tools = asArray(data.tools).map((t) => {
      const rec = asRecord(t)
      if (!rec) return {}
      return {
        id: pickFirstString(rec, ['id']),
        name: pickFirstString(rec, ['name']),
        description: pickFirstString(rec, ['description']),
        badge:
          typeof rec.badge === 'string' || rec.badge === null
            ? (rec.badge as string | null)
            : null
      }
    })

    return {
      ...base,
      action: 'list',
      label: 'Listed tools',
      tools
    }
  }

  if ('tool' in data) {
    const tool = asRecord(data.tool)
    const message = pickFirstString(data, ['message'])?.toLowerCase() ?? ''
    const action =
      key.includes('disableTool') || message.includes('disabled')
        ? 'disable'
        : 'enable'

    return {
      ...base,
      action,
      label: action === 'disable' ? 'Disabled tool' : 'Enabled tool',
      toolName: pickFirstString(tool, ['name']),
      tools: tool
        ? [
            {
              id: pickFirstString(tool, ['id']),
              name: pickFirstString(tool, ['name']),
              description: pickFirstString(tool, ['description']),
              badge: null
            }
          ]
        : undefined
    }
  }

  return base
}

function normalizeVectorMemory(
  key: string,
  rawResult: unknown
): VectorMemoryNormalizedResult {
  const data = asRecord(rawResult)
  const base: VectorMemoryNormalizedResult = {
    kind: 'vector_memory',
    key,
    label: 'Vector memory',
    items: undefined,
    count: undefined,
    raw: rawResult
  }

  if (!data) return base

  const items = asArray(data.items).map((it) => {
    const rec = asRecord(it)
    if (!rec) return {}
    return {
      key: pickFirstString(rec, ['key']),
      content: pickFirstString(rec, ['content']),
      created_at:
        (rec.created_at as string | undefined) ??
        (rec.createdAt as string | undefined),
      updated_at:
        (rec.updated_at as string | undefined) ??
        (rec.updatedAt as string | undefined),
      expiration:
        (rec.expiration as string | null | undefined) ?? null
    }
  })

  const filtered = items.filter(
    (i) => i.key || i.content || i.created_at || i.updated_at
  )

  return {
    ...base,
    items: filtered,
    count:
      typeof data.count === 'number'
        ? (data.count as number)
        : filtered.length || undefined
  }
}

function normalizeSearchList(
  kind: SearchNormalizedResult['kind'],
  key: string,
  rawResult: unknown
): SearchNormalizedResult | null {
  const data = asRecord(rawResult)
  if (!data) return null

  const items = asArray(data.results || data.items || data.messages).map((val) => {
    const rec = asRecord(val)
    if (!rec) return {}
    return {
      id: typeof rec.id === 'string' ? (rec.id as string) : undefined,
      chat_id: typeof rec.chat_id === 'string' ? (rec.chat_id as string) : undefined,
      message_id:
        typeof rec.message_id === 'string'
          ? (rec.message_id as string)
          : typeof rec.id === 'string'
            ? (rec.id as string)
            : undefined,
      title:
        pickFirstString(rec, ['title', 'name']) ??
        undefined,
      snippet:
        pickFirstString(rec, ['snippet', 'preview', 'content']) ?? undefined,
      created_at:
        (rec.created_at as string | undefined) ??
        (rec.createdAt as string | undefined)
    }
  })

  const filtered = items.filter(
    (i) => i.id || i.chat_id || i.message_id || i.title || i.snippet
  )
  if (filtered.length === 0) return null

  const label =
    kind === 'chat_search'
      ? 'Chat search results'
      : kind === 'bookmark_search'
        ? 'Bookmark search results'
        : 'Starred messages'

  return {
    kind,
    key,
    label,
    items: filtered
  }
}

function normalizeSidecarRun(
  key: string,
  raw: unknown
): SidecarRunNormalizedResult | null {
  const result = safeGetResult(raw)
  let data = asRecord(result)

  if (key === 'Sidecar__delegate' && typeof (result as string) === 'string') {
    try {
      const parsed = JSON.parse(result as string) as Record<string, unknown>
      data = asRecord(parsed)
      if (!data) return null
      const status =
        data.status === 'planning' ||
        data.status === 'running' ||
        data.status === 'completed' ||
        data.status === 'error'
          ? data.status
          : 'completed'
      return {
        kind: 'sidecar_run',
        key,
        label: 'Sidecar',
        runId: typeof data.runId === 'string' ? data.runId : undefined,
        status,
        finalAnswer:
          typeof data.finalAnswer === 'string'
            ? data.finalAnswer
            : typeof data.summary === 'string'
              ? data.summary
              : undefined,
        summary: typeof data.summary === 'string' ? data.summary : undefined,
        raw
      }
    } catch {
      return null
    }
  }

  if (!data || typeof data !== 'object') return null

  const plannedWorkers = asArray(data.plannedWorkers).map((w) => {
    const r = asRecord(w)
    const dependsOn = asArray(r?.dependsOn)
      .map((dependencyId) => (typeof dependencyId === 'string' ? dependencyId : ''))
      .filter(Boolean)
    return {
      id: typeof r?.id === 'string' ? r.id : undefined,
      source:
        r?.source === 'built_in_helper' || r?.source === 'custom_helper'
          ? r.source
          : 'ad_hoc',
      helperTemplateId:
        typeof r?.helperTemplateId === 'string' ? r.helperTemplateId : undefined,
      helperId: typeof r?.helperId === 'string' ? r.helperId : undefined,
      title: typeof r?.title === 'string' ? r.title : undefined,
      objective: typeof r?.objective === 'string' ? r.objective : undefined,
      deliverable: typeof r?.deliverable === 'string' ? r.deliverable : undefined,
      dependsOn: dependsOn.length > 0 ? dependsOn : undefined
    }
  })
  const workers = asArray(data.workers).map((w) => {
    const r = asRecord(w)
    return {
      id: typeof r?.id === 'string' ? r.id : undefined,
      source:
        r?.source === 'built_in_helper' || r?.source === 'custom_helper'
          ? r.source
          : 'ad_hoc',
      helperTemplateId:
        typeof r?.helperTemplateId === 'string' ? r.helperTemplateId : undefined,
      helperId: typeof r?.helperId === 'string' ? r.helperId : undefined,
      title: typeof r?.title === 'string' ? r.title : undefined,
      objective: typeof r?.objective === 'string' ? r.objective : undefined,
      deliverable: typeof r?.deliverable === 'string' ? r.deliverable : undefined,
      status: r?.status === 'completed' || r?.status === 'error' ? r.status : undefined,
      finalOutput: typeof r?.finalOutput === 'string' ? r.finalOutput : undefined,
      error: typeof r?.error === 'string' ? r.error : undefined
    }
  })

  return {
    kind: 'sidecar_run',
    key,
    label: 'Sidecar',
    runId: typeof data.runId === 'string' ? data.runId : undefined,
    status:
      data.status === 'planning' || data.status === 'running' || data.status === 'completed' || data.status === 'error'
        ? data.status
        : undefined,
    executionMode:
      data.executionMode === 'parallel' || data.executionMode === 'sequential'
        ? data.executionMode
        : undefined,
    objective: typeof data.objective === 'string' ? data.objective : undefined,
    reason: typeof data.reason === 'string' ? data.reason : undefined,
    finalAnswer: typeof data.finalAnswer === 'string' ? data.finalAnswer : undefined,
    summary: typeof data.summary === 'string' ? data.summary : undefined,
    agentCount: typeof data.agentCount === 'number' ? data.agentCount : undefined,
    successfulAgents: typeof data.successfulAgents === 'number' ? data.successfulAgents : undefined,
    failedAgents: typeof data.failedAgents === 'number' ? data.failedAgents : undefined,
    plannedWorkers: plannedWorkers.length > 0 ? plannedWorkers : undefined,
    workers: workers.length > 0 ? workers : undefined,
    raw
  }
}

/** Pull `![alt](url)` / bare http(s) media URLs out of markdown tool results. */
function extractMediaItemsFromMarkdown(
  text: string,
  kind: MediaGenerationNormalizedResult['kind']
): MediaGenerationNormalizedResult['items'] {
  const items: MediaGenerationNormalizedResult['items'] = []
  const seen = new Set<string>()

  const push = (url: string, prompt?: string, fromMarkdownEmbed = false) => {
    const trimmed = url.trim().replace(/[)\]}>\"']+$/g, '')
    if (!trimmed || seen.has(trimmed)) return
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('/') && !/^data:/i.test(trimmed)) {
      return
    }
    if (kind === 'video_generation') {
      const lower = trimmed.toLowerCase()
      const alt = (prompt || '').toLowerCase()
      const looksVideo =
        /\.(mp4|mov|m4v|webm|gif)(\?|$)/i.test(lower) ||
        lower.includes('video') ||
        lower.includes('generated_videos') ||
        lower.includes('replicate.delivery') ||
        lower.includes('anim.losi.one') ||
        lower.includes('mapi.losi.one') ||
        alt.includes('video') ||
        alt.includes('manim') ||
        alt.includes('animation') ||
        // Video tools often return `![Modified Video](https://…)` without an extension.
        fromMarkdownEmbed
      if (!looksVideo) return
    }
    seen.add(trimmed)
    items.push({ url: trimmed, prompt: prompt?.trim() || undefined })
  }

  const mdImage = /!\[([^\]]*)\]\(([^)\s]+)\)/g
  for (const match of text.matchAll(mdImage)) {
    push(match[2] ?? '', match[1] ?? undefined, true)
  }

  if (items.length === 0) {
    const bare = /https?:\/\/[^\s)\]\">]+/gi
    for (const match of text.matchAll(bare)) {
      push(match[0] ?? '')
    }
  }

  return items
}

function normalizeMediaGeneration(
  kind: MediaGenerationNormalizedResult['kind'],
  key: string,
  rawResult: unknown
): MediaGenerationNormalizedResult | null {
  // unifiedImage / flux / gptImage tools often return markdown:
  // `![prompt](https://…/generated_images/…)\nprompt` — not `{ images: [] }`.
  if (typeof rawResult === 'string') {
    const fromMarkdown = extractMediaItemsFromMarkdown(rawResult, kind)
    if (fromMarkdown.length === 0) return null
    return {
      kind,
      key,
      label: kind === 'image_generation' ? 'Generated images' : 'Generated videos',
      items: fromMarkdown
    }
  }

  const data = asRecord(rawResult)
  if (!data) return null

  const outputs = asArray(data.outputs || data.images || data.items || data.videos)

  const items = outputs
    .map((val) => {
      if (typeof val === 'string') {
        const fromMarkdown = extractMediaItemsFromMarkdown(val, kind)
        return fromMarkdown[0] ?? null
      }
      const rec = asRecord(val)
      if (!rec) return null
      const url =
        pickFirstString(rec, ['url', 'imageUrl', 'videoUrl', 'signedUrl', 'src', 'href']) ?? ''
      if (!url) return null
      const thumb =
        pickFirstString(rec, ['thumbnail', 'thumbnailUrl', 'previewUrl']) ?? undefined
      const prompt =
        pickFirstString(rec, ['prompt']) ??
        pickFirstString(rec, ['description', 'alt', 'title'])
      return {
        url,
        thumbnailUrl: thumb,
        prompt
      }
    })
    .filter((i): i is NonNullable<typeof i> => i != null)

  if (items.length === 0) {
    // Nested `{ result: "![…](url)" }` or `{ url: "…" }` shapes.
    const nestedString =
      (typeof data.result === 'string' && data.result) ||
      (typeof data.output === 'string' && data.output) ||
      (typeof data.markdown === 'string' && data.markdown) ||
      null
    if (nestedString) {
      const fromMarkdown = extractMediaItemsFromMarkdown(nestedString, kind)
      if (fromMarkdown.length > 0) {
        return {
          kind,
          key,
          label: kind === 'image_generation' ? 'Generated images' : 'Generated videos',
          items: fromMarkdown
        }
      }
    }

    const directUrl = pickFirstString(data, [
      'url',
      'imageUrl',
      'image_url',
      'videoUrl',
      'video_url',
      'signedUrl',
      'publicUrl'
    ])
    if (directUrl) {
      items.push({
        url: directUrl,
        prompt:
          pickFirstString(data, ['prompt', 'description', 'title', 'alt']) ?? undefined
      })
    }
  }

  if (items.length === 0) return null

  const label = kind === 'image_generation' ? 'Generated images' : 'Generated videos'

  return {
    kind,
    key,
    label,
    items
  }
}

// --------------

export function normalizeToolResult(
  key: string,
  raw: unknown
): NormalizedToolResult {
  const result = safeGetResult(raw)

  if (key === 'webScraper__googleSearch' || key.includes('googleSearch')) {
    return normalizeWebSearch(key, result)
  }

  if (key === 'webScraper__FetchDataFromUrl') {
    return normalizeFetchedUrl(key, result)
  }

  if (key === 'webScraper__getWeather') {
    return normalizeWeather(key, result)
  }

  if (key === 'yahooFinance__get_stock_data') {
    return normalizeFinanceQuote(key, result)
  }

  if (key.startsWith('VirtualFilesystem__losi__fs_')) {
    const vf = normalizeVirtualFile(key, result)
    if (vf) return vf
  }

  if (key.startsWith('taskAutomation__')) {
    const tasks = normalizeTasks(key, result)
    if (tasks) return tasks
  }

  if (key.startsWith('MemoryTool__')) {
    return normalizeMemory(key, result)
  }

  if (key === 'LosiSearch__searchEverything') {
    const search = normalizeSearchList('unified_search', key, result)
    if (search) return search
  }

  if (key === 'ChatSearch__searchChats') {
    const search = normalizeSearchList('chat_search', key, result)
    if (search) return search
  }

  if (key === 'BookmarkSearch__searchBookmarks') {
    const search = normalizeSearchList('bookmark_search', key, result)
    if (search) return search
  }

  if (key === 'StarredMessagesSearch__searchStarredMessages') {
    const search = normalizeSearchList('starred_search', key, result)
    if (search) return search
  }

  if (
    key.includes('unifiedImage') ||
    key.includes('imageGeneration') ||
    key.includes('imageEdit') ||
    key.includes('imageGenerator') ||
    key.includes('gptImageFull') ||
    key.includes('flux1Pro') ||
    key.includes('stableDiffusion') ||
    key.includes('nanoBanana')
  ) {
    const media = normalizeMediaGeneration('image_generation', key, result)
    if (media) return media
  }

  if (
    key.includes('videoGenerator') ||
    key.includes('studioVideoGenerator') ||
    key.includes('videoGeneration') ||
    key.includes('modifyVideo') ||
    key.includes('manimAnimation') ||
    key.includes('renderManimAnimation')
  ) {
    const media = normalizeMediaGeneration('video_generation', key, result)
    if (media) return media
  }

  if (key === 'Sidecar__delegate' || key === 'sidecar_run') {
    const sidecar = normalizeSidecarRun(key, raw)
    if (sidecar) return sidecar
  }

  if (key.startsWith('ChatManagementTool__')) {
    return normalizeChatManagement(key, result)
  }

  if (
    key === 'createPdfDocument' ||
    key.endsWith('__createPdfDocument') ||
    key.toLowerCase().includes('createpdfdocument')
  ) {
    const data = asRecord(result) ?? {}
    const fileName =
      pickFirstString(data, ['fileName', 'file_name', 'name', 'title']) ||
      'document.pdf'
    const pdfBase64 = pickFirstString(data, ['pdfBase64', 'pdf_base64', 'pdf']) || undefined
    const url =
      pickFirstString(data, ['url', 'downloadUrl', 'download_url']) ||
      (pdfBase64 && !pdfBase64.startsWith('data:') && pdfBase64.length < 2_500_000
        ? `data:application/pdf;base64,${pdfBase64}`
        : undefined)
    return {
      kind: 'pdf_document',
      key,
      label: 'PDF',
      success: typeof data.success === 'boolean' ? data.success : undefined,
      url,
      fileName: fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`,
      message: pickFirstString(data, ['message', 'summary']) || undefined,
      error: pickFirstString(data, ['error']) || undefined,
      pdfBase64,
      raw: result
    } satisfies PdfDocumentNormalizedResult
  }

  if (key.startsWith('ConnectedServices__')) {
    return normalizeConnectedServices(key, result)
  }

  if (key.startsWith('ToolManagementTool__')) {
    return normalizeToolManagement(key, result)
  }

  if (key === 'vectorScratchpadTool') {
    return normalizeVectorMemory(key, result)
  }

  if (key.startsWith('losiSpaces__')) {
    const spaces = normalizeLosiSpaces(key, result)
    if (spaces) return spaces
  }

  return {
    kind: 'generic',
    key,
    label: key,
    raw: result
  }
}
